import type { CALinkType } from '@ds/interfaces'
import { setPreviousRouteUrl } from '@vuejs/utils/stringUtils'
import { defineComponent, type PropType } from 'vue'

import CAIconComponent from '../shared/CAIconComponent'

/**
	* @component
	* The `CALink` component is used to create a text link.
	*
	* @prop {string} [dataTest=''] - The data-test attribute for testing purposes.
	* @prop {boolean} [disabled=false] - Whether the link is disabled.
	* @prop {string} [href=''] - The href for the link.
	* @prop {string} [icon=''] - The icon for the action.
	* @prop {string} [iconRight=''] - The icon for the action, displayed on the right side.
	* @prop {boolean} [nofollow=false] - Whether the link should have a nofollow attribute.
	* @prop {string} [overrideColorClass=''] - The override color class for the link. Refer to the colors in the documentation.
	* @prop {string} [target=''] - The target for the link.
	* @prop {string} [text=''] - The text for the link.
	* @prop {('custom' | 'danger' | 'default' | 'help' | 'info' | 'success')} [type='default'] - The type of the link.
	*
	* @emit {void} click - When the link is clicked.
	*
	* @slot default - The link text.
*/

export default defineComponent({
	name: 'CALink',

	props: {
		/**
		 * The data-test attribute for testing purposes.
		 * @type {string}
		 * @default ''
		 */
		dataTest: { default: '', type: String },
		/**
		 * Whether the link is disabled.
		 * @type {boolean}
		 * @default false
		 */
		disabled: { default: false, type: Boolean },
		/**
		 * The href for the link.
		 * @type {string}
		 * @default ''
		 */
		href: { default: '', type: String },
		/**
		 * The icon for the action.
		 * @type {string}
		 * @default ''
		 */
		icon: { default: '', type: String },
		/**
		 * The icon for the action, displayed on the right side.
		 * @type {string}
		 * @default ''
		 */
		iconRight: { default: '', type: String },
		/**
		 * Whether the link should have a nofollow attribute.
		 * @type {boolean}
		 * @default false
		 */
		nofollow: { default: false, type: Boolean },
		/**
		 * The override color class for the link. Refer to the colors in the documentation.
		 * @type {string}
		 * @default ''
		 */
		overrideColorClass: { default: '', type: String },
		/**
		 * The target for the link.
		 * @type {string}
		 * @default ''
		 */
		target: { default: '', type: String },
		/**
		 * The text for the link.
		 * @type {string}
		 * @default ''
		 */
		text: { default: '', type: String },
		/**
		 * The type of the link.
		 * @type {('custom' | 'danger' | 'default' | 'help' | 'info' | 'success')}
		 * @default 'default'
		 */
		type: { default: 'default', type: String as PropType<CALinkType> },
	},

	emits: ['click'],

	methods: {
		handleClick(event?: Event) {
			if (this.type === 'default' || this.href === '#') {
				event?.preventDefault()
			}

			if (this.disabled) return

			setPreviousRouteUrl()

			if (this.href && this.href !== '#') {
				if (this.target === '_blank') {
					window.open(this.href, '_blank')
				} else {
					window.location.href = this.href
				}
			}

			this.$emit('click')
		},
	},

	render() {
		return (
			<a
				aria-label={this.text}
				class={[
					'ca-link',
					`ca-link__${this.type}`,
					{
						'cursor-pointer': !this.disabled,
						'ca-link__disabled': this.disabled,
						'ca-link--override-color': this.overrideColorClass || this.type === 'custom',
						[this.overrideColorClass]: !!this.overrideColorClass,
					},
				]}
				data-test={this.dataTest}
				data-test-component="ca-link"
				rel={this.nofollow ? 'nofollow' : undefined}
				role="link"
				target={this.target}
				onClick={this.handleClick}
			>
				{this.icon &&
					<CAIconComponent class="ca-link__icon" icon={this.icon} />
				}

				{this.text && <span>{this.text}</span>}
				{this.$slots.default && <span>{this.$slots.default?.()}</span>}

				{this.iconRight &&
					<CAIconComponent
						class="ca-link__icon ca-link__icon-right"
						icon={this.iconRight}
						svg-class="w-3 h-3"
					/>
				}
			</a>
		)
	},
})
